import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { report } from 'lib/utils/dataReport';
import { ATTA_REPORT } from 'lib/constants/atta';
import { AgreementConfig } from 'lib/constants/operator';
import { showLongTextTipsDialog } from 'lib/components/LongTextTipsDialog';
import AgreementText from './AgreementText';
import { buildAgreementReportParams } from './agreementReport';
import './AgreementCheckbox.scss';

/** 协议勾选态变更来源（埋点归因），使用对象常量避免魔法字符串 */
export const AgreementCheckSource = {
  Checkbox: 'checkbox',
  Text: 'text',
  Reconfirm: 'reconfirm',
  Expand: 'expand',
} as const;
export type AgreementCheckSource = typeof AgreementCheckSource[keyof typeof AgreementCheckSource];

export interface AgreementCheckboxRef {
  isChecked: () => boolean;
  /**
   * 设置勾选态。
   * @param checked 目标勾选态
   * @param source  来源标记，仅用于"由 false → true / true → false"且非用户直接交互时（如二次确认弹窗）
   *                的埋点归因；不传则不上报 EVT-21/22，仅静默切换状态。
   */
  setChecked: (checked: boolean, source?: AgreementCheckSource) => void;
}

interface AgreementCheckboxProps {
  config: AgreementConfig;
  packageId?: string;
  /**
   * 预览态（运管 SMSAgreementConfig 在页面里展示的"所见即所得"调试组件）下：
   * - 关闭所有 EVT-20~EVT-29 上报，避免编辑器/调试点击污染合规埋点口径
   * - 勾选交互、文案点击、协议详情弹窗等行为本身保留，方便走查样式
   */
  previewMode?: boolean;
}

/**
 * 翼支付 dxyzf 弹窗专用协议勾选区
 * - 勾选状态遵循 BR-27：仅在本次弹窗生命周期内保持，弹窗关闭后销毁、下次按 defaultChecked 重新初始化
 *   （组件随 SMSCaptchaModal 一起卸载，故无需手动持久化）
 * - 勾选区点击：勾选框本体 → from='checkbox'；文案非超链接区域 → from='text'（BR-24 ②）
 * - 文案过长时折行展示「点击展开」按钮，点击弹出完整协议
 */
const AgreementCheckbox = forwardRef<AgreementCheckboxRef, AgreementCheckboxProps>(
  ({ config, packageId, previewMode = false }, ref) => {
    const [checked, setChecked] = useState<boolean>(Boolean(config.defaultChecked));
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textWrapRef = useRef<HTMLDivElement>(null);

    // EVT-20 协议勾选区曝光（预览态不上报）
    useEffect(() => {
      if (previewMode) return;
      report(ATTA_REPORT.VERIFY.AGREEMENT_VIEW, {
        extraStatisticParams: buildAgreementReportParams(
          { packageId, agreementVersion: config.agreementVersion },
          { defaultChecked: config.defaultChecked ? 1 : 0 },
        ),
      });
    }, [previewMode, packageId, config.agreementVersion, config.defaultChecked]);

    useLayoutEffect(() => {
      const el = textWrapRef.current;
      if (!el) return;
      setIsOverflowing(el.scrollHeight > el.clientHeight);
    }, [config.displayText]);

    const reportAgreementCheckChange = (next: boolean, source: AgreementCheckSource): void => {
      if (previewMode) return;
      const extraParams = buildAgreementReportParams(
        { packageId, agreementVersion: config.agreementVersion },
        next ? { from: source } : undefined,
      );
      report(next ? ATTA_REPORT.VERIFY.AGREEMENT_CHECK : ATTA_REPORT.VERIFY.AGREEMENT_UNCHECK, {
        extraStatisticParams: extraParams,
      });
    };

    /**
     * 勾选状态切换；埋点上报拆到 reportAgreementCheckChange，便于维护
     */
    const updateChecked = (next: boolean, source: AgreementCheckSource): void => {
      if (next === checked) return;
      setChecked(next);
      reportAgreementCheckChange(next, source);
    };

    useImperativeHandle(
      ref,
      () => ({
        isChecked: () => checked,
        setChecked: (next, source) => {
          if (source) {
            updateChecked(next, source);
          } else {
            setChecked(next);
          }
        },
      }),
      // updateChecked 闭包依赖 checked / packageId / agreementVersion，保持新鲜
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [checked, packageId, config.agreementVersion],
    );

    const handleCheckboxClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      updateChecked(!checked, AgreementCheckSource.Checkbox);
    };

    /**
     * 点击文案区域（非超链接）也可切换勾选态（BR-24 ②）
     * AgreementText 内部对 <a> 调用了 stopPropagation，不会冒泡到这里，因此可以安全使用容器级 onClick
     */
    const handleTextAreaClick = () => {
      updateChecked(!checked, AgreementCheckSource.Text);
    };

    const expandDialogContent = useMemo(
      () => (
        <div className="yzf-reconfirm">
          <AgreementText
            config={config}
            packageId={packageId}
            previewMode={previewMode}
            className="yzf-agreement-dialog__text"
            style={{ marginTop: '0.2rem' }}
          />
        </div>
      ),
      [config, packageId, previewMode],
    );

    const handleExpand = (e: React.MouseEvent) => {
      e.stopPropagation();
      showLongTextTipsDialog({
        title: config.reconfirmTitle || '温馨提示',
        content: expandDialogContent,
        buttonText: config.reconfirmConfirmText || '同意并继续',
        onConfirm: () => {
          updateChecked(true, AgreementCheckSource.Expand);
        },
      });
    };

    return (
      <div className="yzf-agreement-checkbox">
        <div
          className={cx('yzf-agreement-checkbox__input', {
            'yzf-agreement-checkbox__input--checked': checked,
          })}
          onClick={handleCheckboxClick}
          role="checkbox"
          aria-checked={checked}
          tabIndex={0}
        />
        <div className="yzf-agreement-checkbox__body" onClick={handleTextAreaClick}>
          <div
            ref={textWrapRef}
            className={cx('yzf-agreement-checkbox__text-wrap', {
              'yzf-agreement-checkbox__text-wrap--overflowing': isOverflowing,
            })}
          >
            <AgreementText
              config={config}
              packageId={packageId}
              previewMode={previewMode}
              className="yzf-agreement-checkbox__text"
            />
          </div>
          {isOverflowing && (
            <span className="yzf-agreement-checkbox__expand-btn" onClick={handleExpand}>
              点击展开
            </span>
          )}
        </div>
      </div>
    );
  },
);

AgreementCheckbox.displayName = 'AgreementCheckbox';

export default AgreementCheckbox;
